import React, { useEffect, useState } from 'react';
import Modal from './Modal';

export interface SimpleFormField {
  name: string;
  label: string;
  type?: 'text' | 'number' | 'textarea';
  placeholder?: string;
  required?: boolean;
  step?: string;
  min?: string;
  rows?: number;
}

interface SimpleFormModalProps {
  isOpen: boolean;
  title: string;
  fields: SimpleFormField[];
  initialValues?: Record<string, string>;
  submitLabel?: string;
  loading?: boolean;
  onSubmit: (values: Record<string, string>) => void;
  onClose: () => void;
}

const SimpleFormModal: React.FC<SimpleFormModalProps> = ({
  isOpen,
  title,
  fields,
  initialValues,
  submitLabel = 'Submit',
  loading = false,
  onSubmit,
  onClose
}) => {
  const [values, setValues] = useState<Record<string, string>>(initialValues || {});

  useEffect(() => {
    if (isOpen) {
      setValues(initialValues || {});
    }
  }, [isOpen, initialValues]);

  const handleChange = (name: string, value: string): void => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (event: React.FormEvent): void => {
    event.preventDefault();
    onSubmit(values);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="small">
      <form onSubmit={handleSubmit}>
        {fields.map((field) => (
          <div key={field.name} className="form-group">
            <label>{field.label}{field.required ? ' *' : ''}</label>
            {field.type === 'textarea' ? (
              <textarea
                rows={field.rows || 4}
                value={values[field.name] || ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
                placeholder={field.placeholder}
                required={field.required}
              />
            ) : (
              <input
                type={field.type || 'text'}
                value={values[field.name] || ''}
                onChange={(e) => handleChange(field.name, e.target.value)}
                placeholder={field.placeholder}
                required={field.required}
                step={field.step}
                min={field.min}
              />
            )}
          </div>
        ))}
        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading && <span className="btn-spinner" aria-hidden="true"></span>}
            {submitLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default SimpleFormModal;
